import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Watch,
  Event,
  EventEmitter,
} from "@stencil/core";

import {
  HeaderInterface,
  NavigateEvent,
} from "../../services/header-interface/header-interface.service";
import { Bundle, bundleForLanguage } from "../../services/i18n";
import { createLoginUrl } from "../../services/oidc/login";
import { logout } from "../../services/oidc/logout";
import { AdobeAnalyticsTrackingService } from "../../services/tracking/adobe-analytics-tracking.service";
import { CustomerState, isCustomer, TLanguage } from "../../types";
import { Item } from "@swisscom/sdx";

const TRACKING_CATEGORY = "l1-link";
const TRACKING_LOCATION = "header";

declare global {
  interface Window {
    saveStateAndTriggerLogin?(): void;
  }
}

export interface UserMenuLink extends Item {
  show?:
    | "always"
    | "logged-out"
    | "logged-in"
    | "logged-in-res"
    | "logged-in-biz"
    | "logged-in-biz-user";
}

interface StaticLoginConfig {
  href: string;
  returnUrlParameter?: string;
}

interface OIDCLoginConfig {
  configUrl?: string;
}

interface PrefixLoginConfig {
  prefix: string;
}

export type LoginConfig =
  | StaticLoginConfig
  | OIDCLoginConfig
  | PrefixLoginConfig;

@Component({
  tag: "scs-user-info",
  styleUrl: "user-info.scss",
  shadow: false,
})
export class UserInfo {
  @Element() host!: HTMLElement;
  private sdxHeader!: HeaderInterface;

  /**
   * The language to use for i18n strings.
   */
  @Prop()
  public language = (document.documentElement.lang || "de") as TLanguage;

  private i18n!: Bundle;
  private loginConfigParsed: LoginConfig = {};

  @Prop()
  public customer: CustomerState = "LOADING";

  /**
   * A list of custom links to display inside the dropdown displayed for logged-in users.
   */
  @Prop()
  public userMenuLinks: UserMenuLink[] | string = "[]";

  /**
   * Heading to display above the custom links.
   */
  @Prop()
  public linkHeading?: string;

  /**
   * Heading to display above the custom links when logged-out. Will use `link-heading` if unset.
   */
  @Prop()
  public linkHeadingAnonymous?: string;

  /**
   * Link href to use for the logout button.
   *
   * Alternatively, you can listen to the `performLogout` event.
   */
  @Prop()
  public logoutHref?: string;

  /**
   * Configuration for login mechanism.
   * Defaults to oidc (with config at /scel/uber_oauth/.well-known/openid-configuration)
   */
  @Prop()
  public loginConfig?: string | LoginConfig;

  /**
   * Triggered when the user clicks the log out button (only available when logged in)
   */
  @Event()
  public performLogout!: EventEmitter<void>;

  private adobeAnalyticsTrackingService: AdobeAnalyticsTrackingService;

  public constructor() {
    this.adobeAnalyticsTrackingService =
      AdobeAnalyticsTrackingService.getInstance();
  }

  public connectedCallback() {
    this.sdxHeader = new HeaderInterface(this.host.closest("sdx-header")!);
  }

  public async componentWillLoad() {
    this.sdxHeader.attachNavigateListener(this.handleNavigate.bind(this));
    window.addEventListener("trigger-login", this.handleLogin.bind(this));

    this.i18n = await bundleForLanguage(this.language);
    await this.updateHeader();
    this.updateLoginConfig(this.loginConfig);
  }

  async componentWillUpdate(): Promise<void> {
    if (this.language !== this.i18n.lang) {
      this.i18n = await bundleForLanguage(this.language);
    }
    await this.updateHeader();
  }

  private async updateHeader() {
    const customer = this.customer;
    if (customer === "LOADING") {
      this.sdxHeader.setLoginData();
      return;
    }
    const loggedIn = isCustomer(customer);
    const menuLinks: UserMenuLink[] =
      typeof this.userMenuLinks === "string"
        ? JSON.parse(this.userMenuLinks)
        : this.userMenuLinks;
    const links = this.applicableUserLinks(menuLinks);
    this.sdxHeader.setLoginData({
      customer: loggedIn
        ? {
            fullName: customer.companyName || (customer.name as string),
            statusIconName: customer.priorityStatus ? "icon-crown" : undefined,
            statusLabel: customer.priorityStatus
              ? {
                  de: "Priorité Status",
                  en: "Priorité Status",
                  fr: "Statut Priorité",
                  it: "Status Priorité",
                }[this.language]
              : undefined,
          }
        : undefined,
      logout: {
        href: this.logoutHref,
        dataset: {
          isLogout: "true",
          trackCategory: TRACKING_CATEGORY,
          trackAction: "link",
          trackLocation: TRACKING_LOCATION,
          trackLabel: "logout",
        },
      },
      login: {
        href: loggedIn ? undefined : "#",
        dataset: {
          isLogin: "true",
          trackCategory: TRACKING_CATEGORY,
          trackAction: "link",
          trackLocation: TRACKING_LOCATION,
          trackLabel: "login",
        },
      },
      dataset: {
        trackAction: "link",
        trackLocation: TRACKING_LOCATION,
        trackCategory: TRACKING_CATEGORY,
        trackLabel: "open",
      },
      children: [
        {
          label:
            (this.customer === "ANONYMOUS"
              ? this.linkHeadingAnonymous
              : undefined) ?? this.linkHeading,
          children: links,
        },
      ],
    });
  }

  @Watch("loginConfig")
  public updateLoginConfig(newValue?: string | LoginConfig) {
    if (newValue && typeof newValue === "object") {
      this.loginConfigParsed = newValue;
    } else {
      this.loginConfigParsed = JSON.parse(newValue || "{}") as LoginConfig;
    }
  }

  private handleNavigate(event: NavigateEvent) {
    if (event.detail.item?.dataset?.["isLogin"] === "true") {
      event.preventDefault();
      if (window.saveStateAndTriggerLogin) {
        window.saveStateAndTriggerLogin();
      } else {
        this.handleLogin();
      }
    } else if (event.detail.item?.dataset?.["isLogout"] === "true") {
      event.preventDefault();
      this.handleLogout(event.detail.item);
    }
  }

  private async handleLogin() {
    let href = "";
    if ("prefix" in this.loginConfigParsed) {
      let prefix = this.loginConfigParsed.prefix || "/";
      if (!prefix.startsWith("/")) {
        prefix = "/" + prefix;
      }
      if (prefix.endsWith("/")) {
        prefix = prefix.slice(0, -1);
      }
      href =
        prefix +
        window.location.pathname +
        window.location.search +
        window.location.hash;
    } else if ("href" in this.loginConfigParsed) {
      const url = new URL(this.loginConfigParsed.href, window.location.href);
      if (this.loginConfigParsed.returnUrlParameter) {
        url.searchParams.set(
          this.loginConfigParsed.returnUrlParameter,
          window.location.href,
        );
      }
      href = String(url);
    } else {
      href = await createLoginUrl(this.loginConfigParsed.configUrl);
    }
    window.location.href = href;
  }

  private handleLogout(item: Item) {
    this.adobeAnalyticsTrackingService.trackByDataset(item.dataset);
    const performLogoutEvent = this.performLogout.emit();

    const oidcConfigUrl =
      "configUrl" in this.loginConfigParsed
        ? this.loginConfigParsed.configUrl
        : undefined;
    logout(oidcConfigUrl).finally(() => {
      if (!performLogoutEvent.defaultPrevented && this.logoutHref) {
        window.location.href = this.logoutHref;
      }
    });
  }

  private applicableUserLinks(menuLinks: UserMenuLink[] = []): Item[] {
    return menuLinks
      .filter(({ show }) => {
        if (!show || show === "always") {
          return true;
        }
        if (show === "logged-out" && this.customer === "ANONYMOUS") {
          return true;
        }
        if (!isCustomer(this.customer)) {
          return false;
        }
        if (show === "logged-in") {
          return true;
        }
        if (show === "logged-in-res" && this.customer.segment === "res") {
          return true;
        }
        if (show === "logged-in-biz" && this.customer.segment !== "res") {
          return true;
        }
        if (show === "logged-in-biz-user" && this.customer.userName) {
          return true;
        }
        return false;
      })
      .map(({ show, ...link }) => link);
  }

  render() {
    return <Host></Host>;
  }
}
